import React, { useState, useMemo } from 'react';
import { useCollection } from '../firebase-files/hooks/useCollection';
import './PricePage.scss';

const PricePage = () => {
  // Fetch categories
  const { documents: categories, error: categoriesError } = useCollection('m_categories');
  // Selected category state (store both id and name for filtering)
  const [selectedCategory, setSelectedCategory] = useState(null); // {id, category}

  // Get subcategories for selected category
  const subCategories = useMemo(() => {
    if (!categories || !selectedCategory) return [];
    const found = categories.find(cat => cat.id === selectedCategory.id);
    return found && Array.isArray(found.subCategories) ? found.subCategories : [];
  }, [categories, selectedCategory]);

  // Selected subcategory name
  const [selectedSubCatName, setSelectedSubCatName] = useState(null);

  // All products
  const { documents: allProducts, error: productsError } = useCollection('products', null);
  const isLoadingCat = !categories && !categoriesError;
  const isLoadingProducts = !allProducts && !productsError;

  // Filtering logic:
  // When a category is selected: show all products where product.category (array) includes selectedCategory.category (name)
  // When a subcategory is selected too: filter further where product.subCategory (array) includes the selectedSubCatName
  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];
    if (!selectedCategory) return [];
    let filtered = allProducts.filter(prod =>
      Array.isArray(prod.category) && prod.category.includes(selectedCategory.category)
    );
    if (selectedSubCatName) {
      filtered = filtered.filter(prod =>
        Array.isArray(prod.subCategory) && prod.subCategory.includes(selectedSubCatName)
      );
    }
    return filtered;
  }, [allProducts, selectedCategory, selectedSubCatName]);

  // Default select the first category
  React.useEffect(() => {
    if (!categories || categories.length === 0) return;
    if (!selectedCategory) setSelectedCategory({ id: categories[0].id, category: categories[0].category });
  }, [categories, selectedCategory]);

  // Default select first subcategory if changed
  React.useEffect(() => {
    if (subCategories && subCategories.length > 0) {
      setSelectedSubCatName(subCategories[0]?.subCategoryName);
    } else {
      setSelectedSubCatName(null);
    }
  }, [selectedCategory, subCategories]);

  return (
    <div className="price-page-root">   
      <div className="price-page-header">
        <h1>Price Page</h1>
        <p>This is the price page for the products</p>
      </div>
      <div className="price-page-container">
        <div className="price-page-col price-page-col-categories">
          <div className="price-page-category-list">
            {isLoadingCat ? (
              <div className="price-page-loading">Loading...</div>
            ) : categoriesError ? (
              <div className="price-page-error">{categoriesError}</div>
            ) : (
              categories && categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`price-page-category-item${selectedCategory && cat.id === selectedCategory.id ? ' selected' : ''}`}
                  onClick={() => setSelectedCategory({ id: cat.id, category: cat.category })}
                  tabIndex={0}
                >
                  {cat.category}
                </div>
              ))
            )}
          </div>
        </div>
        <div className="price-page-col price-page-col-content">
          <div className="price-page-subcategory-wrap">
            {subCategories.length ? (
              <div className="price-page-subcategory-list">
                {subCategories.map(sc => (
                  <div
                    className={`price-page-subcategory-item${selectedSubCatName === sc.subCategoryName ? ' selected' : ''}`}
                    key={sc.subCategoryName}
                    onClick={() => setSelectedSubCatName(sc.subCategoryName)}
                    tabIndex={0}
                  >
                    {sc.subCategoryName}
                  </div>
                ))}
              </div>
            ) : <div className="price-page-no-subcategory">No subcategories</div>}
          </div>
          <div className="price-page-products-wrap">
            {isLoadingProducts ? (
              <div className="price-page-loading">Loading...</div>
            ) : productsError ? (
              <div className="price-page-error">{productsError}</div>
            ) : filteredProducts.length === 0 ? (
              <div className="price-page-empty">No products found</div>
            ) : (
              <div className="price-page-products-list">
                {filteredProducts.map(prod => (
                  <div className="price-page-product" key={prod.id}>
                    <div className="price-page-product-name">{prod.name}</div>
                    <div className="price-page-product-price">₹{prod.price}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricePage;
